import { SEAT_CIRCLE_RADIUS } from '../constants';
import { degreesToRadians, radiansToDegrees, round } from './utils';
export const getClosestAngle = (fromDegrees, toDegrees, targetDegrees) => {
    let from = (fromDegrees - targetDegrees) % 360;
    let to = (toDegrees - targetDegrees) % 360;
    from = from < 0 ? from + 360 : from;
    to = to < 0 ? to + 360 : to;
    if (from > to) {
        return targetDegrees;
    }
    const fromDiff = from;
    const toDiff = 360 - to;
    if (fromDiff < toDiff) {
        return (from + targetDegrees) % 360;
    }
    return (to + targetDegrees) % 360;
};
export const getCirclePoint = (degrees, center, radius) => {
    const angle = degreesToRadians(radius > 0 ? degrees : 360 - degrees);
    const x = center.x + radius * Math.sin(angle);
    const y = center.y - radius * Math.cos(angle);
    return { x, y };
};
export const getDegreesBetweenPoints = (a, angleCorner, c) => {
    const ba = { x: a.x - angleCorner.x, y: a.y - angleCorner.y };
    const bc = { x: c.x - angleCorner.x, y: c.y - angleCorner.y };
    const dotProduct = ba.x * bc.x + ba.y * bc.y;
    const magnitudeBA = Math.sqrt(ba.x * ba.x + ba.y * ba.y);
    const magnitudeBC = Math.sqrt(bc.x * bc.x + bc.y * bc.y);
    const cosTheta = dotProduct / (magnitudeBA * magnitudeBC);
    const angleRadians = Math.acos(cosTheta);
    const angleDegrees = radiansToDegrees(angleRadians);
    return angleDegrees;
};
export const getCircleIntersections = (circles) => {
    const dx = circles[1].center.x - circles[0].center.x;
    const dy = circles[1].center.y - circles[0].center.y;
    const dist = Math.hypot(dy, dx);
    if (dist > circles[0].radius + circles[1].radius) {
        return [];
    }
    if (dist < Math.abs(circles[0].radius - circles[1].radius)) {
        return [];
    }
    if (circles[0].center.x === circles[1].center.x &&
        circles[0].center.y === circles[1].center.y &&
        circles[0].radius === circles[1].radius) {
        throw new Error('Comparing two equivalent circles');
    }
    const centroid = (Math.pow(circles[0].radius, 2) - Math.pow(circles[1].radius, 2) + Math.pow(dist, 2)) / (2 * dist);
    const x2 = circles[0].center.x + (dx * centroid) / dist;
    const y2 = circles[0].center.y + (dy * centroid) / dist;
    const h = Math.sqrt(Math.pow(circles[0].radius, 2) - Math.pow(centroid, 2));
    const rx = -dy * (h / dist);
    const ry = dx * (h / dist);
    const pointA = {
        x: Number((x2 + rx).toFixed(15)),
        y: Number((y2 + ry).toFixed(15)),
    };
    const pointB = {
        x: Number((x2 - rx).toFixed(15)),
        y: Number((y2 - ry).toFixed(15)),
    };
    const result = [pointA];
    if (pointB.x !== pointA.x || pointB.y !== pointA.y) {
        result.push(pointB);
    }
    return result;
};
export const changeDegreesByFraction = (degrees, fraction) => {
    let normalizedDegrees = degrees % 360;
    if (normalizedDegrees < 0) {
        normalizedDegrees += 360;
    }
    if (normalizedDegrees <= 180) {
        return round(normalizedDegrees * fraction, 0.00001);
    }
    const result = 360 - (360 - normalizedDegrees) * fraction;
    return result;
};
export const generateCurvedRowPath = ({ midX, midY: originalMidY, curveRadius, curveAngleFrom: curveAngleFromOriginal, curveAngleTo, }) => {
    const curveAngleFrom = curveAngleFromOriginal > curveAngleTo ? curveAngleFromOriginal - 2 * Math.PI : curveAngleFromOriginal;
    const midY = originalMidY + curveRadius;
    const topRadius = curveRadius + SEAT_CIRCLE_RADIUS;
    const bottomRadius = curveRadius - SEAT_CIRCLE_RADIUS;
    const polarToCartesian = (cx, cy, radius, angle) => {
        const adjustedAngle = angle - Math.PI / 2;
        return [cx + radius * Math.cos(adjustedAngle), cy + radius * Math.sin(adjustedAngle)];
    };
    // Top arc
    const [topStartX, topStartY] = polarToCartesian(midX, midY, topRadius, curveAngleFrom);
    const [topEndX, topEndY] = polarToCartesian(midX, midY, topRadius, curveAngleTo);
    // Bottom arc
    const [bottomStartX, bottomStartY] = polarToCartesian(midX, midY, bottomRadius, curveAngleFrom);
    const [bottomEndX, bottomEndY] = polarToCartesian(midX, midY, bottomRadius, curveAngleTo);
    // Angular span for large-arc-flag
    const angleSpan = curveAngleTo - curveAngleFrom;
    const largeArcFlag = angleSpan > Math.PI ? 1 : 0;
    const d = [
        // Start
        `M ${topStartX},${topStartY}`,
        // Top arc
        `A ${topRadius},${topRadius} 0 ${largeArcFlag},1 ${topEndX},${topEndY}`,
        // Right side
        `L ${bottomEndX},${bottomEndY}`,
        // Bottom arc
        `A ${bottomRadius},${bottomRadius} 0 ${largeArcFlag},0 ${bottomStartX},${bottomStartY}`,
        // Left side
        `L ${topStartX},${topStartY}`,
        // Close path explicitly
        `Z`,
    ].join(' ');
    return d;
};
export const rotatePoint = (targetPoint, referencePoint, angle) => {
    const angleCos = Math.cos(-angle);
    const angleSin = Math.sin(-angle);
    const dx = targetPoint.x - referencePoint.x;
    const dy = targetPoint.y - referencePoint.y;
    const rotatedX = angleCos * dx + angleSin * dy + referencePoint.x;
    const rotatedY = angleCos * dy - angleSin * dx + referencePoint.y;
    return { x: rotatedX, y: rotatedY };
};
//# sourceMappingURL=geometry.js.map